/**
 * Контекст подання для картки (UX-05; shell-navigation.md): з якого подання відкрито картку і
 * знімок порядку його записів на момент відкриття — для крихти «<Подання> › <Запис>», лічильника
 * «8 з 86» і `J` / `K`. Живе у стані запису історії (`history.state`), а не в адресі: адреса
 * картки лишається стабільною (`/r/<сутність>/<id>`), а відкрита за посиланням картка просто не
 * має контексту — крихта веде на список сутності, лічильника немає.
 *
 * Знімок — ідентифікатори вже завантажених сторінок (без окремого запиту) і курсор наступної:
 * на межі сторінки `J` дозавантажує її. Права — у DAL: у знімок потрапляють лише доступні записи.
 * Знімок тримає не більше `MAX_SNAPSHOT` id: браузер серіалізує стан на кожен крок `J`, тож при
 * дозавантаженні відкидаємо найдавніші, а `offset` зберігає номер першого в поданні.
 *
 * Курсор несе значення сортування, а не умову (E4a-W4): дозавантаження йде тим самим `POST …/query`
 * з умовою й сортуванням списку зі знімка. Умову на поля `personal` / `sensitive` стан історії не
 * несе (як і адреса, SEC-110): у такого знімка немає курсора, `J` іде лише завантаженими рядками.
 *
 * Тут — те, що бере перший екран (список кладе знімок у стан історії): типи, `historyState`,
 * `historyIndexOf`. Розбір знімка й кроки картки (`listContextOf`, `positionIn`, `extendContext`…)
 * — у `card/list-nav.ts`: картка лінива, тож у бюджеті першого екрана їх немає.
 */
import type { FilterNode } from '@clientline/contracts';
import type { HistoryState } from '@tanstack/react-router';

/** Скільки id знімка тримає стан історії. */
export const MAX_SNAPSHOT = 1000;

export interface ListContext {
  /** Ключ подання з `/meta`. */
  view: string;
  /** Знімок порядку: id записів завантажених сторінок. */
  ids: readonly string[];
  /** Курсор наступної сторінки; `null` — сторінок більше немає. */
  cursor: string | null;
  /**
   * Сортування списку, якщо людина змінила його в шапці (`<поле>:asc|desc`, LST-10): курсор несе
   * значення саме цього сортування, тож дозавантаження йде з ним.
   */
  sort?: string;
  /**
   * Умова списку (E4a-W4): дерево чи `null` — без умови. Немає — знімок старої версії: умова
   * подання за його `scope`.
   */
  filter?: FilterNode | null;
  /** Напрям останнього кроку `J` / `K`: видалений запис пропускаємо в тому ж напрямі. */
  dir?: 1 | -1;
  /** Скільки записів подання перед першим id знімка (відкинуті найдавніші). */
  offset?: number;
  /**
   * Індекс запису історії списку (`__TSR_index`), з якого відкрито картку. «Назад» до списку —
   * лише коли картка стоїть рівно наступним записом; інакше — перехід на адресу списку.
   */
  from?: number;
  /**
   * Картку відкрито з дошки (KAN-12): ключ воронки. Знімок — завантажені картки того самого
   * стовпця в порядку дошки, без курсора; повернення — на дошку цієї воронки.
   */
  board?: string;
}

/** Сортування в адресі запиту: ключ поля й напрям (той самий формат, що `sort=` API). */
export const SORT_VALUE = /^[a-z][a-z0-9_]{0,62}:(?:asc|desc)$/;

/** Стан запису історії застосунку. */
export interface AppHistoryState {
  listContext?: ListContext | undefined;
  /** Перехід — повернення до екрана (крихта, `Esc`): відновити прокрутку й фокус (UX-08). */
  returnState?: boolean;
}

/** Стан для `Link state` / `navigate({ state })`: роутер зберігає його у `history.state`. */
export const historyState = (state: AppHistoryState): HistoryState => state as HistoryState;

/** Індекс запису історії (`__TSR_index`, ставить історія роутера). */
export const historyIndexOf = (state: unknown): number | undefined => {
  const index = (state as { __TSR_index?: unknown } | undefined)?.__TSR_index;
  return typeof index === 'number' ? index : undefined;
};
